import React, { useEffect, useState } from 'react';
import { Col, DatePicker, Divider, Form, Input, InputNumber, Modal, Row, Select, Spin, Typography, message } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';

/**
 * 27.10.2026: YS kabin kaydı (ys_sozlesme_kayit) düzenleme penceresi. Yeni sistemde ysk yalnız listeleniyordu;
 * fatura ekranı kısayolundan (/yer_sozlesme_detay/{söz}?ysk={id}) ya da sözleşme detayındaki satırdan açılır.
 * Uçlar: GET /form/ysk/{id}, PUT /form/ysk-guncelle/{id} (toplam_hisse sunucuda yeniden hesaplanır).
 */
const { Text } = Typography;
const AYLAR = [{ value: 0, label: 'Boş (başlangıç ayı)' }].concat(
  ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']
    .map((label, i) => ({ value: i + 1, label })));
const HISSEDARLAR = [1, 2, 3, 4, 5, 6];
const buYil = new Date().getFullYear();
const YILLAR = [buYil - 3, buYil - 2, buYil - 1, buYil, buYil + 1].filter((y) => y >= 2013 && y <= 2028);
const tarihMi = (v) => v && !String(v).startsWith('0000');
const paraGirdisi = {
  min: 0, style: { width: '100%' }, decimalSeparator: ',',
  formatter: (v) => (v === undefined || v === null || v === '' ? '' : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')),
  parser: (v) => (v || '').replace(/\./g, '').replace(',', '.'),
};
const yetki = () => {
  const t = localStorage.getItem('auth_token');
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

export default function YskDuzenle({ apiBase, yskId, onClose, onKaydedildi }) {
  const [form] = Form.useForm();
  const [kayit, setKayit] = useState(null);
  const [kaydediliyor, setKaydediliyor] = useState(false);

  useEffect(() => {
    setKayit(null);
    if (!yskId) return;
    axios.get(`${apiBase}/form/ysk/${yskId}`, yetki())
      .then((r) => setKayit(r.data))
      .catch((e) => { message.error(e?.response?.data?.detail || 'YS kabin kaydı yüklenemedi'); onClose(); });
  }, [apiBase, yskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const kaydet = async () => {
    const v = await form.validateFields();
    setKaydediliyor(true);
    try {
      await axios.put(`${apiBase}/form/ysk-guncelle/${yskId}`,
        { ...v, kiraBaslangic: v.kiraBaslangic ? v.kiraBaslangic.format('YYYY-MM-DD') : null }, yetki());
      message.success('YS kabin kaydı güncellendi');
      onKaydedildi?.();
      onClose();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setKaydediliyor(false);
    }
  };

  const ilk = kayit ? { ...kayit, kiraBaslangic: tarihMi(kayit.kiraBaslangic) ? dayjs(kayit.kiraBaslangic) : null } : null;
  return (
    <Modal open={!!yskId} width={820} destroyOnClose onCancel={onClose} onOk={kaydet} confirmLoading={kaydediliyor}
      okText="Kaydet" cancelText="Vazgeç" okButtonProps={{ disabled: !kayit }}
      title={`YS Kabin Kaydı Düzenle — #${yskId || ''}${kayit ? ` (lokasyon ${kayit.lokasyon}, sözleşme ${kayit.sozlesme})` : ''}`}>
      {!ilk ? <Spin /> : (
        <Form form={form} layout="vertical" requiredMark="optional" initialValues={ilk} preserve={false}>
          <Row gutter={12}>
            <Col xs={12} sm={4}><Form.Item name="kabin" label="Kabin"><InputNumber min={0} max={50} style={{ width: '100%' }} /></Form.Item></Col>
            <Col xs={12} sm={6}>
              <Form.Item name="kiraBaslangic" label="Kira Başlangıç" rules={[{ required: true, message: 'Tarih seçin' }]}>
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={7}><Form.Item name="odeme_ay" label="Ödeme (Artış) Ayı"><Select options={AYLAR} /></Form.Item></Col>
            <Col xs={12} sm={7}><Form.Item name="bos_odeme_ay" label="Boş Kabin Ödeme Ayı"><Select options={AYLAR} /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col xs={12} sm={8}><Form.Item name="kiraBedeli" label="Kira Bedeli"><InputNumber {...paraGirdisi} /></Form.Item></Col>
            <Col xs={12} sm={8}><Form.Item name="bankaGirdikce" label="Banka Girdikçe Bedeli"><InputNumber {...paraGirdisi} /></Form.Item></Col>
          </Row>
          <Divider orientation="left" plain style={{ margin: '4px 0 8px' }}>Hissedarlar</Divider>
          <Row gutter={12}>
            {HISSEDARLAR.map((i) => (
              <Col xs={24} sm={12} key={i}>
                <Row gutter={8}>
                  <Col span={17}><Form.Item name={`${i}_sahip`} label={`${i}. Sahip`}><Input maxLength={255} /></Form.Item></Col>
                  <Col span={7}><Form.Item name={`${i}_hisse`} label="Hisse"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                </Row>
              </Col>
            ))}
          </Row>
          <Divider orientation="left" plain style={{ margin: '4px 0 8px' }}>Yıllık Tutarlar (hesap bazı)</Divider>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Hesap en son dolu yılın tutarını baz alıp sonraki yıllara artış uygular.
          </Text>
          <Row gutter={12}>
            {YILLAR.map((y) => (
              <Col xs={12} sm={Math.floor(24 / YILLAR.length)} key={y}>
                <Form.Item name={`${y}_kira`} label={`${y} Kira`}><InputNumber {...paraGirdisi} /></Form.Item>
                <Form.Item name={`${y}_bos`} label={`${y} Boş`}><InputNumber {...paraGirdisi} /></Form.Item>
              </Col>
            ))}
          </Row>
        </Form>
      )}
    </Modal>
  );
}
